import React, { useEffect, useState } from 'react';
import { 
  Plus, Edit2, Trash2, Loader, AlertCircle, Check, Tag, X, 
  ToggleLeft, ToggleRight, Calendar, Hash, Percent, FileText, 
  Activity, Shield, ChevronRight, ArrowUpRight, Zap, Ban
} from 'lucide-react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import alertService from '../utils/alert';
import { cn } from '../utils/cn';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import { useCompanyCurrency } from '../hooks/useCompanySettings';

const EMPTY_FORM = {
  code: '', description: '', discountType: 'percentage',
  discountValue: '', expiryDate: '', usageLimit: '', isActive: true,
  applicablePlans: [],
};

const fieldCls = [
  'w-full px-3 py-2.5 rounded-lg text-sm',
  'bg-[var(--ap-surface2)] border border-[var(--ap-border)]',
  'text-[var(--ap-text)] placeholder:text-[var(--ap-text-dim)]',
  'outline-none focus:border-[var(--ap-primary)] transition-colors',
].join(' ');

function fmt(date) {
  return date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No Expiry';
}

function isExpired(date) {
  return date && new Date(date) < new Date();
}

export default function AdminCoupons() {
  const companyCurrency = useCompanyCurrency();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData,      setFormData]      = useState(EMPTY_FORM);
  const [plans, setPlans] = useState([]);

  useEffect(() => { loadCoupons(); loadPlans(); }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const res = await api.get('/coupons');
      setCoupons(res.data.coupons || []);
    } catch (err) {
      alertService.error('Connection Error', 'Failed to load coupon registry.');
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await api.get('/admin/plans');
      setPlans(res.data.plans || []);
    } catch (err) {
      // Non-fatal — plan restriction picker just won't be available
    }
  };

  const togglePlan = (planId) => {
    setFormData(p => ({
      ...p,
      applicablePlans: p.applicablePlans.includes(planId)
        ? p.applicablePlans.filter(id => id !== planId)
        : [...p.applicablePlans, planId],
    }));
  };

  const openCreate = () => {
    setEditingCoupon(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditingCoupon(c);
    setFormData({
      code: c.code, description: c.description || '',
      discountType: c.discountType, discountValue: c.discountValue,
      expiryDate: c.expiryDate ? c.expiryDate.slice(0, 10) : '',
      usageLimit: c.usageLimit ?? '', isActive: c.isActive,
      applicablePlans: (c.applicablePlans || []).map(p => p._id || p),
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        discountValue: Number(formData.discountValue),
        usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
      };
      if (editingCoupon) {
        const res = await api.put(`/coupons/${editingCoupon._id}`, payload);
        setCoupons(prev => prev.map(c => c._id === editingCoupon._id ? res.data.coupon : c));
        alertService.success('Update Success', 'Coupon details updated successfully.');
      } else {
        const res = await api.post('/coupons', payload);
        setCoupons(prev => [res.data.coupon, ...prev]);
        alertService.success('Creation Success', 'New promo coupon created.');
      }
      setShowModal(false);
    } catch (err) {
      alertService.error('Operation Failed', err.response?.data?.message || 'Failed to save coupon data.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await alertService.confirm(
      'Delete Coupon?',
      'This action will permanently remove this coupon code. Proceed?',
      'danger',
      'Delete'
    );
    if (!confirmed) return;

    try {
      await api.delete(`/coupons/${id}`);
      setCoupons(prev => prev.filter(c => c._id !== id));
      alertService.success('Deleted', 'Coupon removed from database.');
    } catch (err) {
      alertService.error('Delete Failed', err.response?.data?.message || 'Failed to delete coupon.');
    }
  };

  const handleToggle = async (c) => {
    try {
      const res = await api.put(`/coupons/${c._id}`, {
        ...c,
        isActive: !c.isActive,
        applicablePlans: (c.applicablePlans || []).map(p => p._id || p),
      });
      setCoupons(prev => prev.map(x => x._id === c._id ? res.data.coupon : x));
      alertService.success('Status Updated', `Coupon ${!c.isActive ? 'activated' : 'deactivated'}.`);
    } catch {
      alertService.error('Update Failed', 'Status synchronization failure.');
    }
  };

  if (loading) return (
    <div className="w-full py-40 flex flex-col items-center justify-center gap-6">
       <Loader size={32} className="animate-spin text-[#0176D3]" />
       <div className="text-[11px] font-bold text-[#706E6B]">Loading Promo Coupons…</div>
    </div>
  );

  return (
    <div className="w-full pb-10">
      <PageHeader
        title="Promo Coupons"
        icon={Tag}
        subtitle={`Manage discount codes and promotional billing campaigns · ${coupons.length} coupons configured`}
        actions={<Button variant="primary" size="sm" icon={Plus} onClick={openCreate}>New Coupon</Button>}
      />

      <Card className="p-0 overflow-hidden bg-white border-[#E5E5E5]">
        <div className="px-4 py-4 border-b border-[#D8DDE6] flex items-center justify-between bg-white ">
           <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#0176D3]/10 border border-[#0176D3]/20 flex items-center justify-center text-[#0176D3]">
                 <Hash size={18} />
              </div>
              <h2 className="text-sm font-bold text-[#080707] tracking-tight">Active Coupons</h2>
           </div>
           <div className="text-[11px] font-bold text-[#080707] flex items-center gap-4">
              <span className="flex items-center gap-2 text-[#2E844A]"><div className="w-2 h-2 rounded-full bg-[#2E844A] " /> ACTIVE</span>
              <span className="flex items-center gap-2 text-[#BA0517]"><div className="w-2 h-2 rounded-full bg-[#BA0517] opacity-50" /> EXPIRED</span>
           </div>
        </div>

        <div className="overflow-x-auto scrollbar-hide">
          {coupons.length === 0 ? (
            <div className="py-40 flex flex-col items-center justify-center text-center space-y-6 opacity-30">
               <div className="w-20 h-20 rounded-lg bg-[#F4F6F9] flex items-center justify-center text-[#080707]">
                  <Tag size={40} strokeWidth={1} />
               </div>
               <div className="space-y-1">
                  <h3 className="text-lg font-bold text-[#080707] tracking-tight">No Promo Data</h3>
                  <p className="text-[11px] font-bold text-[#080707] max-w-xs mx-auto leading-relaxed">No discount patterns detected in the current administration frame.</p>
               </div>
            </div>
          ) : (
            <div className="slds2-table-wrap overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#D8DDE6] text-[11px] font-bold text-[#706E6B] bg-[#ffffff]/20">
                  <th className="pl-8 py-5">Coupon Code</th>
                  <th className="py-5">Discount Value</th>
                  <th className="py-5">Expiry Date</th>
                  <th className="py-5">Usage Status</th>
                  <th className="py-5">Platform Status</th>
                  <th className="pr-8 py-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.02]">
                {coupons.map(c => {
                  const expired = isExpired(c.expiryDate);
                  const limitReached = c.usageLimit !== null && c.usedCount >= c.usageLimit;
                  return (
                    <tr key={c._id} className={cn(
 "group hover:bg-white/[0.02] transition-colors",
 (!c.isActive || expired) && "opacity-40 grayscale-[0.5]"
 )}>
                      <td className="pl-8 py-5">
                         <div className="space-y-2">
                            <span className="inline-block px-3 py-1 rounded-lg bg-[#0176D3]/10 border border-[#0176D3]/20 text-[13px] font-bold text-[#0176D3] tracking-tighter transition-transform">
                               {c.code}
                            </span>
                            {c.description && <div className="text-[11px] font-bold text-[#080707] pl-1 truncate max-w-[150px]">{c.description}</div>}
                            <div className="text-[11px] font-bold text-[#706E6B] pl-1 truncate max-w-[150px]">
                              {c.applicablePlans?.length > 0 ? c.applicablePlans.map(p => p.name).join(', ') : 'All Plans'}
                            </div>
                         </div>
                      </td>
                      <td className="py-5">
                         <div className="text-sm font-bold text-[#080707] tracking-tighter tabular-nums">
                            {c.discountType === 'percentage' ? (
                               <span className="flex items-center gap-2 text-[#0176D3] "><Percent size={14} /> {c.discountValue}% Off</span>
                            ) : (
                               <span className="flex items-center gap-2 text-[#2E844A] ">{formatCurrency(c.discountValue, companyCurrency)} Flat</span>
                            )}
                         </div>
                      </td>
                      <td className="py-5">
                        <div className="text-[11px] font-bold text-[#080707] relative">
                          {fmt(c.expiryDate)}
                          {expired && <span className="absolute -bottom-4 left-0 text-[11px] font-bold text-[#BA0517] animate-pulse">EXPIRED</span>}
                        </div>
                      </td>
                      <td className="py-5">
                        <div className="text-xs font-bold text-[#706E6B] tabular-nums">
                          {c.usedCount} <span className="text-[#080707]">/</span> {c.usageLimit || '∞'}
                          {limitReached && <span className="block text-[11px] font-bold text-[#DD7A01]">LIMIT REACHED</span>}
                        </div>
                      </td>
                      <td className="py-5">
                        <button
                          className={cn(
 "flex items-center gap-3 px-4 py-2 rounded whitespace-nowrap text-[13px] font-semibold border transition-all active:scale-95",
 c.isActive ? 'bg-[#2E844A]/10 text-[#2E844A] border-[#2E844A]/20 ' : 'bg-[#F4F6F9] text-[#706E6B] border-[#D8DDE6]'
 )}
                          onClick={() => handleToggle(c)}
                        >
                          {c.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                          {c.isActive ? 'Active' : 'Disabled'}
                        </button>
                      </td>
                      <td className="pr-8 py-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                           <Button variant="outline" size="sm" icon={Edit2} onClick={() => openEdit(c)} />
                           <button 
                             className="w-10 h-10 rounded bg-[#BA0517]/5 border border-[#BA0517]/10 flex items-center justify-center text-[#BA0517]/40 hover:text-[#BA0517] hover:bg-[#BA0517]/10 hover:border-[#BA0517]/20 transition-all active:scale-90" 
                             title="Delete Coupon" aria-label="Delete Coupon"
                             onClick={() => handleDelete(c._id)}
                           >
                             <Trash2 size={18} />
                           </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingCoupon ? 'Edit Coupon' : 'Create Coupon'}
        maxWidth="2xl"
      >
        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-5 space-y-8">
            <div className="flex items-center gap-6 border-b border-[#D8DDE6] pb-8">
              <div className="w-16 h-16 rounded-[1.8rem] bg-[#0176D3]/10 border border-[#0176D3]/20 flex items-center justify-center text-[#0176D3] shadow-sm shadow-blue-500/20 ">
                  <Tag size={32} strokeWidth={1.5} />
              </div>
              <div className="space-y-1">
                  <h2 className="text-2xl font-bold text-[#080707] tracking-tighter leading-none">{editingCoupon ? 'Edit Coupon' : 'New Coupon'}</h2>
                  <p className="text-[11px] font-bold text-[#080707] ">{editingCoupon ? `Modifying: ${editingCoupon.code}` : 'Configure new discount parameters'}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ">
              <Input
                label="Coupon Code *"
                value={formData.code}
                icon={ArrowUpRight}
                onChange={e => setFormData(p => ({ ...p, code: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                placeholder="E.G. SAVE50"
                disabled={!!editingCoupon}
                required
              />
              <Input
                label="Description"
                value={formData.description}
                icon={FileText}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Promo campaign name"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ">
              <div className="space-y-3">
                <label className="text-[12px] font-semibold text-[#706E6B] ml-1 flex items-center gap-2">Discount Type *</label>
                <select className="w-full h-9 px-6 rounded bg-white/[0.03] border border-[#D8DDE6] text-[#080707] appearance-none cursor-pointer focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3] outline-none" value={formData.discountType} onChange={e => setFormData(p => ({ ...p, discountType: e.target.value }))} required>
                  <option value="percentage">Percentage Off (%)</option>
                  <option value="fixed">Fixed Amount ({getCurrencySymbol(companyCurrency)})</option>
                </select>
              </div>
              <Input
                label="Discount Value *"
                type="number"
                value={formData.discountValue}
                icon={Hash}
                onChange={e => setFormData(p => ({ ...p, discountValue: e.target.value }))}
                placeholder="Value amount"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ">
              <Input
                label="Expiry Date *"
                type="date"
                value={formData.expiryDate}
                icon={Calendar}
                min={new Date().toISOString().slice(0, 10)}
                onChange={e => setFormData(p => ({ ...p, expiryDate: e.target.value }))}
                required
              />
              <Input
                label="Usage Limit"
                type="number"
                value={formData.usageLimit}
                icon={Shield}
                onChange={e => setFormData(p => ({ ...p, usageLimit: e.target.value }))}
                placeholder="Leave blank for unlimited"
              />
            </div>

            <div className="space-y-3">
              <label className="text-[12px] font-semibold text-[#706E6B] ml-1 flex items-center gap-2">Applicable Plans</label>
              <p className="text-[11px] font-bold text-[#706E6B] ">Leave all unchecked to allow this coupon on any plan.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {plans.map(plan => (
                  <label
                    key={plan._id}
                    className={cn(
 "flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer text-[12px] font-semibold ",
 formData.applicablePlans.includes(plan._id) ? "bg-[#0176D3]/10 border-[#0176D3]/30 text-[#0176D3]" : "bg-white/[0.02] border-[#D8DDE6] text-[#080707]"
 )}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={formData.applicablePlans.includes(plan._id)}
                      onChange={() => togglePlan(plan._id)}
                    />
                    {plan.name}
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-4 group cursor-pointer p-5 rounded-lg bg-white/[0.02] border border-[#D8DDE6] hover:bg-white/[0.04] transition-all">
              <div className={cn(
 "w-10 h-6 rounded-full relative transition-colors",
 formData.isActive ? "bg-[#2E844A]" : "bg-[#F4F6F9]"
 )}>
                <div className={cn(
 "absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow-[0_2px_8px_rgba(0,0,0,0.16)]",
 formData.isActive ? "left-5" : "left-1"
 )} />
              </div>
              <input
                type="checkbox"
                className="hidden"
                checked={formData.isActive}
                onChange={e => setFormData(p => ({ ...p, isActive: e.target.checked }))}
              />
              <span className="text-[11px] font-bold text-[#080707] ">Enable Coupon Sync (Active)</span>
            </label>

            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1 h-16" variant="primary" loading={saving} icon={Check}>
                {editingCoupon ? 'Save Changes' : 'Create Coupon'}
              </Button>
              <Button type="button" variant="outline" className="flex-1 h-16" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
